import { memo, ReactNode } from 'react';
import { cj, cm } from 'src/utils/classNames';
import useNumberInput from 'src/components/primitives/Input/hooks/useNumberInput';
import useTextInput from 'src/components/primitives/Input/hooks/useTextInput';
import {
	BaseInputProps,
	InputSize,
} from 'src/components/primitives/Input/types';
import InputUi from 'src/components/primitives/Input/InputUi';

type SharedProps = BaseInputProps & {
	id?: string;
	/**
	 * If `headless`, returns only an unstyled `input` with the necessary props applied.
	 * Defaults to `default`.
	 */
	variant?: 'default' | 'headless';
	/**
	 * Defaults to `sm`.
	 */
	size?: InputSize;
	label?: ReactNode;
	error?: ReactNode;
	/**
	 * Test ID for Playwright/testing
	 */
	dataTestId?: string;
};

type InputWithStandardLabelProps = {
	id: string;
	labelVariant: 'standard';
	label: ReactNode;
	prefix?: ReactNode;
};

type InputWithNoLabelProps = {
	id?: string;
	labelVariant: 'none';
	label?: never;
	prefix?: ReactNode;
};

// Note: `inset` is the default label variant, so we want these types to also
// apply when it's `undefined`.
type InputWithInsetLabelProps = {
	id: string;
	labelVariant?: 'inset' | undefined;
	label: ReactNode;
	prefix?: never;
};

type HeadlessInputProps = {
	variant: 'headless';
	labelVariant?: never;
	label?: never;
	prefix?: never;
};

export type InputProps = SharedProps &
	(
		| InputWithStandardLabelProps
		| InputWithInsetLabelProps
		| InputWithNoLabelProps
		| HeadlessInputProps
	);

function Input({
	size = 'sm',
	type = 'number',
	variant = 'default',
	labelVariant = 'inset',
	id,
	value,
	placeholder,
	onChange,
	onClick,
	onFocus,
	onBlur,
	onEnter,
	onEscape,
	onKeyUp,
	validate,
	autoComplete,
	ref,
	error,
	allowNegative,
	numberFormatProps,
	stepSize,
	enableSelectAllOnFocus,
	prefix,
	suffix,
	disabled,
	label,
	containerClassName,
	labelClassName,
	inputWrapperClassName,
	prefixClassName,
	inputClassName,
	suffixClassName,
	errorClassName,
	dataTestId,
	...rest
}: InputProps) {
	const numberInputProps = useNumberInput({
		value,
		placeholder,
		onChange,
		onClick,
		onFocus,
		onBlur,
		onEnter,
		onEscape,
		onKeyUp,
		validate,
		allowNegative,
		numberFormatProps,
		stepSize,
		enableSelectAllOnFocus,
		autoComplete,
		ref,
	});

	const textInputProps = useTextInput({
		value,
		placeholder,
		onChange,
		onClick,
		onFocus,
		onBlur,
		onEnter,
		onEscape,
		onKeyUp,
		autoComplete,
		ref,
	});

	const inputProps = type === 'number' ? numberInputProps : textInputProps;

	if (variant === 'headless') {
		return (
			<input
				className={inputClassName}
				disabled={disabled}
				data-testid={dataTestId}
				{...rest}
				{...inputProps}
			/>
		);
	}

	return (
		<InputUi.StackedWrapper
			className={cj('group/input-field', containerClassName)}
			disabled={disabled}
		>
			{labelVariant === 'standard' && (
				<InputUi.StandardLabel
					htmlFor={id}
					className={cm('typo-label-xs text-text-secondary', labelClassName)}
				>
					{label}
				</InputUi.StandardLabel>
			)}
			<InputUi.Container
				className={cj(
					!error && 'has-[input:focus]:border-button-primary-bg',
					inputWrapperClassName
				)}
				disabled={disabled}
				error={!!error}
			>
				{labelVariant === 'inset' && (
					<InputUi.InsetLabel
						className={cj(size === 'sm' && 'pr-sm', labelClassName)}
						disabled={disabled}
						size={size}
						asChild
					>
						<label htmlFor={id}>{label}</label>
					</InputUi.InsetLabel>
				)}
				{prefix && (
					<InputUi.Prefix
						className={cj(size === 'sm' && 'pr-sm', prefixClassName)}
						size={size}
					>
						{prefix}
					</InputUi.Prefix>
				)}
				<InputUi.Value
					className={cj(
						suffix && 'pr-0',
						(prefix || labelVariant === 'inset') && 'pl-0 text-right',
						inputClassName
					)}
					size={size}
					disabled={disabled}
					asChild
				>
					<input
						id={id}
						data-invalid={!!error}
						data-testid={dataTestId}
						disabled={disabled}
						{...rest}
						{...inputProps}
					/>
				</InputUi.Value>
				{suffix && (
					<InputUi.Suffix
						className={cj(size === 'sm' && 'pl-sm', suffixClassName)}
						size={size}
					>
						{suffix}
					</InputUi.Suffix>
				)}
			</InputUi.Container>
			{error && (
				<InputUi.Error
					className={errorClassName}
					dataTestId={dataTestId ? `${dataTestId}-error` : undefined}
				>
					{error}
				</InputUi.Error>
			)}
		</InputUi.StackedWrapper>
	);
}

export default memo(Input);
